// ============================================================================
// DynamicField — per-type field renderer
// ============================================================================
// Dispatches on `field.type` and renders the appropriate input. Receives the
// current value, onChange callback, and an optional field-level error string.
// Used by:
//   - <DynamicForm /> for top-level fields
//   - <DynamicGrid />'s row editor for grid row fields
//
// Styling follows the project's form-input / form-field-error conventions.
// SyutSelect handles its own error display via the `error` prop.

import React, { useEffect, useRef, useState } from 'react';
import { SyutSelect } from 'components/ui';
import type {
  BooleanFieldSpec,
  CurrencyFieldSpec,
  DateFieldSpec,
  DatetimeFieldSpec,
  FieldSpec,
  FormValues,
  GridRowFieldSpec,
  NumberFieldSpec,
  RefFieldSpec,
  SecuritySelectFieldSpec,
  SelectFieldSpec,
  SelectOption,
  TextFieldSpec,
  TextareaFieldSpec,
} from 'api/types/formSpec.types';
import type { CaseSecurity } from 'api/types/caseSecurity.types';
import { caseSecurityService } from 'api/services/caseSecurityService';
import { resolveOptions } from './optionsResolver';
import { evalBool } from './expressionEvaluator';
import type { SeedContext } from './seedResolver';

// Resolve a field's effective label, honouring `dynamic_label` (first entry
// whose expression is true against the current values wins). Cheap; returns the
// same field identity when there is no override so React memoisation is intact.
function withDynamicLabel(
  field: FieldSpec | GridRowFieldSpec,
  allValues?: FormValues,
): FieldSpec | GridRowFieldSpec {
  const rules = field.dynamic_label;
  if (!rules || rules.length === 0) return field;
  const match = rules.find((r) => evalBool(r.if, allValues ?? {}, false));
  if (!match || match.label === field.label) return field;
  return { ...field, label: match.label } as FieldSpec | GridRowFieldSpec;
}

export interface DynamicFieldProps {
  field: FieldSpec | GridRowFieldSpec;
  value: unknown;
  onChange: (next: unknown) => void;
  error?: string;
  disabled?: boolean;
  /** When the parent form wants to skip rendering (e.g., grid handles its own UX). */
  hideLabel?: boolean;
  /** Full form values — used by ref fields that need cross-field context. */
  allValues?: FormValues;
  /** Opaque runtime context (e.g. `{ caseId }`) — used by case-scoped fields. */
  seedContext?: SeedContext;
}

const DynamicField: React.FC<DynamicFieldProps> = (rawProps) => {
  const field = withDynamicLabel(rawProps.field, rawProps.allValues);
  const props = { ...rawProps, field };
  switch (field.type) {
    case 'text':     return <TextInput     {...props} field={field as TextFieldSpec} />;
    case 'textarea': return <TextareaInput {...props} field={field as TextareaFieldSpec} />;
    case 'number':   return <NumericInput  {...props} field={field as NumberFieldSpec} isCurrency={false} />;
    case 'currency': return <NumericInput  {...props} field={field as unknown as NumberFieldSpec} isCurrency={true} />;
    case 'date':     return <DateInput     {...props} field={field as DateFieldSpec} />;
    case 'datetime': return <DatetimeInput {...props} field={field as DatetimeFieldSpec} />;
    case 'boolean':  return <BooleanInput  {...props} field={field as BooleanFieldSpec} />;
    case 'select':   return <SelectInput   {...props} field={field as SelectFieldSpec} />;
    case 'ref':      return <RefInput      {...props} field={field as RefFieldSpec} />;
    case 'security_select': return <SecuritySelect {...props} field={field as SecuritySelectFieldSpec} />;
    case 'grid':     return null; // Grids are rendered by <DynamicGrid> at the parent level
    default:
      // eslint-disable-next-line no-console
      console.warn('[DynamicField] unsupported type', (field as any).type);
      return null;
  }
};

// ----------------------------------------------------------------------------
// Shared layout: label + required indicator + error message
// ----------------------------------------------------------------------------

const FieldShell: React.FC<{
  field: FieldSpec | GridRowFieldSpec;
  error?: string;
  hideLabel?: boolean;
  children: React.ReactNode;
}> = ({ field, error, hideLabel, children }) => (
  <div className="form-group" data-field={field.key} style={fieldGroupStyle(field)}>
    {!hideLabel && (
      <label className="form-label">
        {field.label}
        {field.required && <span className="text-danger"> *</span>}
      </label>
    )}
    {children}
    {field.description && (
      <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
        {field.description}
      </div>
    )}
    {error && <div className="form-error-message">{error}</div>}
  </div>
);

const fieldGroupStyle = (field: FieldSpec | GridRowFieldSpec): React.CSSProperties => {
  if (!field.width) return {};
  if (field.width === 'full') return { gridColumn: '1 / -1' };
  return { width: field.width };
};

// ----------------------------------------------------------------------------
// Renderers — one per field type
// ----------------------------------------------------------------------------

interface TypedProps<T> extends Omit<DynamicFieldProps, 'field'> {
  field: T;
}

const TextInput: React.FC<TypedProps<TextFieldSpec>> = ({ field, value, onChange, error, disabled, hideLabel }) => (
  <FieldShell field={field} error={error} hideLabel={hideLabel}>
    <input
      type="text"
      className={`form-input${error ? ' form-field-error' : ''}`}
      value={value == null ? '' : String(value)}
      maxLength={field.max_length}
      placeholder={field.placeholder}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
    />
  </FieldShell>
);

const TextareaInput: React.FC<TypedProps<TextareaFieldSpec>> = ({ field, value, onChange, error, disabled, hideLabel }) => (
  <FieldShell field={field} error={error} hideLabel={hideLabel}>
    <textarea
      className={`form-input${error ? ' form-field-error' : ''}`}
      rows={field.rows ?? 3}
      maxLength={field.max_length}
      placeholder={field.placeholder}
      disabled={disabled}
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value)}
    />
  </FieldShell>
);

const NumericInput: React.FC<TypedProps<NumberFieldSpec> & { isCurrency: boolean }> = ({
  field, value, onChange, error, disabled, hideLabel, isCurrency,
}) => (
  <FieldShell field={field} error={error} hideLabel={hideLabel}>
    <div style={{ position: 'relative' }}>
      {isCurrency && (
        <span style={{
          position: 'absolute', left: '0.5rem', top: '50%', transform: 'translateY(-50%)',
          color: 'var(--text-secondary)', fontSize: '0.85rem', pointerEvents: 'none',
        }}>₹</span>
      )}
      <input
        type="number"
        className={`form-input${error ? ' form-field-error' : ''}`}
        style={isCurrency ? { paddingLeft: '1.5rem' } : undefined}
        min={field.min}
        max={field.max}
        step={field.step}
        placeholder={field.placeholder}
        disabled={disabled}
        value={value == null || value === '' ? '' : String(value)}
        onChange={(e) => {
          const raw = e.target.value;
          onChange(raw === '' ? null : Number(raw));
        }}
      />
    </div>
  </FieldShell>
);

const DateInput: React.FC<TypedProps<DateFieldSpec>> = ({ field, value, onChange, error, disabled, hideLabel }) => (
  <FieldShell field={field} error={error} hideLabel={hideLabel}>
    <input
      type="date"
      className={`form-input${error ? ' form-field-error' : ''}`}
      min={field.min}
      max={field.max}
      disabled={disabled}
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value || null)}
    />
  </FieldShell>
);

const DatetimeInput: React.FC<TypedProps<DatetimeFieldSpec>> = ({ field, value, onChange, error, disabled, hideLabel }) => (
  <FieldShell field={field} error={error} hideLabel={hideLabel}>
    <input
      type="datetime-local"
      className={`form-input${error ? ' form-field-error' : ''}`}
      min={field.min}
      max={field.max}
      disabled={disabled}
      value={value == null ? '' : String(value)}
      onChange={(e) => onChange(e.target.value || null)}
    />
  </FieldShell>
);

const BooleanInput: React.FC<TypedProps<BooleanFieldSpec>> = ({ field, value, onChange, error, disabled, hideLabel }) => {
  const checked = value === true;
  const display = field.display ?? 'checkbox';

  if (display === 'yesno_radio') {
    return (
      <FieldShell field={field} error={error} hideLabel={hideLabel}>
        <div style={{ display: 'flex', gap: '1rem', paddingTop: '0.25rem' }}>
          <label style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', cursor: disabled ? 'default' : 'pointer' }}>
            <input type="radio" disabled={disabled} checked={value === true} onChange={() => onChange(true)} /> Yes
          </label>
          <label style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', cursor: disabled ? 'default' : 'pointer' }}>
            <input type="radio" disabled={disabled} checked={value === false} onChange={() => onChange(false)} /> No
          </label>
        </div>
      </FieldShell>
    );
  }

  // Default: inline checkbox; label rendered alongside the box
  return (
    <FieldShell field={field} error={error} hideLabel={true}>
      <label style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
        cursor: disabled ? 'default' : 'pointer', paddingTop: '0.25rem',
      }}>
        <input
          type="checkbox"
          disabled={disabled}
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>{field.label}{field.required && <span className="text-danger"> *</span>}</span>
      </label>
    </FieldShell>
  );
};

const toOptions = (raw: Array<SelectOption | string>): SelectOption[] =>
  raw.map((o) => typeof o === 'string' ? { value: o, label: o } : o);

const SelectInput: React.FC<TypedProps<SelectFieldSpec>> = ({ field, value, onChange, error, disabled, hideLabel }) => {
  const options = toOptions(field.options ?? []);
  if (field.display === 'radio') {
    return (
      <FieldShell field={field} error={error} hideLabel={hideLabel}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', paddingTop: '0.25rem' }}>
          {options.map((opt) => (
            <label key={opt.value} style={{ display: 'inline-flex', gap: '0.35rem', alignItems: 'center', cursor: disabled ? 'default' : 'pointer' }}>
              <input
                type="radio"
                disabled={disabled}
                checked={value === opt.value}
                onChange={() => onChange(opt.value)}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </FieldShell>
    );
  }

  return (
    <FieldShell field={field} error={error} hideLabel={true}>
      <SyutSelect
        label={hideLabel ? undefined : (
          field.required ? <>{field.label} <span className="text-danger">*</span></> : field.label
        )}
        value={value == null ? '' : String(value)}
        onChange={(v) => onChange((v as string) || null)}
        options={options}
        error={error}
        isDisabled={disabled}
        size="sm"
      />
    </FieldShell>
  );
};

// ----------------------------------------------------------------------------
// RefInput — async-loaded options from a master via optionsResolver
// ----------------------------------------------------------------------------

const RefInput: React.FC<TypedProps<RefFieldSpec>> = ({ field, value, onChange, error, disabled, hideLabel }) => {
  const [options, setOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    resolveOptions(field.options_source, { filter: field.options_filter }).then((opts) => {
      if (!cancelled) setOptions(opts);
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [field.options_source, field.options_filter]);

  return (
    <FieldShell field={field} error={error} hideLabel={true}>
      <SyutSelect
        label={hideLabel ? undefined : (
          field.required ? <>{field.label} <span className="text-danger">*</span></> : field.label
        )}
        value={value == null ? '' : String(value)}
        onChange={(v) => onChange((v as string) || null)}
        options={options}
        error={error}
        isDisabled={disabled || loading}
        isLoading={loading}
        size="sm"
        placeholder={loading ? 'Loading…' : (options.length === 0 ? 'No options available' : 'Select…')}
      />
    </FieldShell>
  );
};

// ----------------------------------------------------------------------------
// SecuritySelect — multi-select checkbox list of THIS case's securities
// ----------------------------------------------------------------------------
// Reads `caseId` from seedContext, loads lit.tbl_case_security rows, and
// renders one checkbox per linked asset. Stored value is a string[] of the
// selected securityIds. On a fresh entry it pre-ticks the in-scope securities
// (unless auto_select_in_scope is false), so the common single-vehicle case
// needs zero clicks.

const securityLabel = (s: CaseSecurity): string => {
  if (s.vehicleRegistrationNo) {
    const desc = [s.vehicleMake, s.vehicleModel].filter(Boolean).join(' ');
    return desc ? `${s.vehicleRegistrationNo} · ${desc}` : s.vehicleRegistrationNo;
  }
  return s.description || s.securityCode || s.securityType;
};

const securitySubLabel = (s: CaseSecurity): string => {
  const bits: string[] = [s.securityCode];
  if (s.securityValue != null) bits.push(`₹${Number(s.securityValue).toLocaleString('en-IN')}`);
  return bits.filter(Boolean).join(' · ');
};

const SecuritySelect: React.FC<TypedProps<SecuritySelectFieldSpec>> = ({
  field, value, onChange, error, disabled, hideLabel, seedContext,
}) => {
  const caseId = typeof seedContext?.caseId === 'string' ? seedContext.caseId : undefined;
  const [securities, setSecurities] = useState<CaseSecurity[]>([]);
  const [loading, setLoading] = useState(false);

  const selected: string[] = Array.isArray(value) ? (value as string[]) : [];

  // Refs so the load effect can run on [caseId] only without stale closures.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;
  const autoSelectedRef = useRef(false);

  useEffect(() => {
    if (!caseId) return;
    let cancelled = false;
    setLoading(true);
    caseSecurityService.getByCase(caseId).then((rows) => {
      if (cancelled) return;
      setSecurities(rows);
      // Pre-tick in-scope assets once, only for a fresh (unsaved) entry where
      // no value has been set yet. An existing saved [] means "explicitly none".
      if (
        field.auto_select_in_scope !== false &&
        !autoSelectedRef.current &&
        valueRef.current === undefined
      ) {
        autoSelectedRef.current = true;
        const inScopeIds = rows.filter((r) => r.inScope).map((r) => r.securityId);
        if (inScopeIds.length > 0) onChangeRef.current(inScopeIds);
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [caseId, field.auto_select_in_scope]);

  const toggle = (securityId: string) => {
    if (disabled) return;
    const set = new Set(selected);
    if (set.has(securityId)) set.delete(securityId);
    else set.add(securityId);
    onChange(Array.from(set));
  };

  return (
    <FieldShell field={field} error={error} hideLabel={hideLabel}>
      {loading ? (
        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', padding: '0.5rem 0' }}>
          Loading securities…
        </div>
      ) : securities.length === 0 ? (
        <div style={{
          padding: '0.6rem 0.75rem', fontSize: '0.8rem', color: 'var(--text-secondary)',
          fontStyle: 'italic', background: 'var(--bg-secondary)',
          border: '1px dashed var(--form-border-color)', borderRadius: 4,
        }}>
          {caseId ? 'No securities linked to this case.' : 'Case context unavailable.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
          {securities.map((s) => {
            const checked = selected.includes(s.securityId);
            return (
              <label
                key={s.securityId}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
                  padding: '0.5rem 0.65rem', borderRadius: 4,
                  border: `1px solid ${checked ? '#2563eb' : 'var(--form-border-color)'}`,
                  background: checked ? 'rgba(37,99,235,0.06)' : 'var(--bg-primary)',
                  cursor: disabled ? 'default' : 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  disabled={disabled}
                  checked={checked}
                  onChange={() => toggle(s.securityId)}
                  style={{ marginTop: '0.15rem' }}
                />
                <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {securityLabel(s)}
                    {s.isPrimary && (
                      <span style={{
                        marginLeft: '0.45rem', fontSize: '0.65rem', fontWeight: 700,
                        color: '#2563eb', background: 'rgba(37,99,235,0.12)',
                        padding: '0.05rem 0.35rem', borderRadius: 3, verticalAlign: 'middle',
                      }}>PRIMARY</span>
                    )}
                    {!s.inScope && (
                      <span style={{
                        marginLeft: '0.45rem', fontSize: '0.65rem', fontWeight: 700,
                        color: 'var(--text-secondary)', background: 'var(--bg-secondary)',
                        padding: '0.05rem 0.35rem', borderRadius: 3, verticalAlign: 'middle',
                      }}>OUT OF SCOPE</span>
                    )}
                  </span>
                  <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                    {securitySubLabel(s)}
                  </span>
                </span>
              </label>
            );
          })}
        </div>
      )}
    </FieldShell>
  );
};

export default DynamicField;
